import type { IngredientConsumption } from './production-consumption.js';

// T-452 (collettiva): costruisce gli `inputs` di tracciabilità di UN piatto a
// partire dal fabbisogno della ricetta (per-piatto) e dai lotti dello SCARICO
// magazzino del giorno. È l'aggancio "in reverse": lo scarico (recipe→lotti FEFO)
// diventa la filiera dietro il QR (piatto → ingredienti → lotti + allergeni).
//
// SCELTA HACCP (owner-confermata 2026-07-12): lo scarico è aggregato per
// ingrediente sul giorno → NON si inventa quale lotto è andato in quale piatto.
// Sotto un ingrediente compaiono TUTTI i suoi lotti scaricati; la qty è a livello
// ingrediente (fabbisogno del piatto), i lotti sono identità (l'origine
// fornitore/fattura si risolve al render via lot-origin). Vedi spec §Modello dati.

/** Riferimento a un lotto sotto un ingrediente (identità; origine risolta al render). */
export interface DischargeLotRef {
  batch_lot: string | null;
  expiration_date: string | null;
}

/** Un input = UN ingrediente del piatto con i suoi lotti (riga madre + sotto-righe). */
export interface DishIngredientInput {
  food_id: string;
  food_name: string;
  /** fabbisogno del piatto per l'ingrediente (netto × porzioni) — a livello ingrediente. */
  qty_used: number;
  uom: string;
  /** allergeni dell'ingrediente (best-effort; fallback live foods.allergens al render). */
  allergens: string[];
  /** TUTTI i lotti scaricati per l'ingrediente (multi-lotto). Vuoto = da completare. */
  lots: DischargeLotRef[];
}

export interface DishInputsMeta {
  foodName: (foodId: string) => string;
  allergens: (foodId: string) => string[];
}

export function buildDishIngredientInputs(
  demand: IngredientConsumption[],
  dischargeByFood: Map<string, DischargeLotRef[]>,
  meta: DishInputsMeta,
): DishIngredientInput[] {
  return demand.map((d) => ({
    food_id: d.food_id,
    food_name: meta.foodName(d.food_id),
    qty_used: d.qty,
    uom: d.uom,
    allergens: meta.allergens(d.food_id),
    lots: dischargeByFood.get(d.food_id) ?? [],
  }));
}

/** Unione (dedup) degli allergeni di una lista di input — per productions.allergens_aggregated. */
export function aggregateInputAllergens(inputs: DishIngredientInput[]): string[] {
  const set = new Set<string>();
  for (const i of inputs) for (const a of i.allergens) if (a) set.add(a);
  return [...set].sort();
}

// T-452: espande gli input multi-lotto (nested `lots[]`) in righe FLAT — una per
// lotto — per i renderer che mostrano un lotto per riga (Scheda, TraceView).
// Input legacy (senza `lots`) restano invariati. Il QR pubblico (trace-page) fa
// invece il raggruppamento madre/sotto-righe; qui basta una riga per lotto.
export function flattenInputLots<
  T extends { batch_lot?: string | null; expiration_date?: string | null; lots?: Array<{ batch_lot?: string | null; expiration_date?: string | null }> | null },
>(inputs: T[]): T[] {
  return inputs.flatMap((i) =>
    i.lots && i.lots.length
      ? i.lots.map((lot) => ({ ...i, batch_lot: lot.batch_lot ?? null, expiration_date: lot.expiration_date ?? i.expiration_date ?? null, lots: undefined }))
      : [i],
  );
}
